import CaseClassificationModal from "@/components/case-classification-modal";
import CaseDeferredModal from "@/components/case-deferred-modal";
import { Tiptap } from "@/components/common/tiptap/Tiptap";
import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import {
	Form,
	FormDescription,
	FormField,
	FormGroup,
	FormIcon,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { IconFileTypePdf } from "@tabler/icons-react";
import { IconAlertCircle } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";

export const Route = createFileRoute("/_dashboard/case-processing_/$caseId")({
	component: RouteComponent,
});

function RouteComponent() {
	const modal = useModal();
	const queryParams = new URLSearchParams(window.location.search);
	const status = queryParams.get("status");
	const remarks = "Lorem Ipsum dolor sit";

	const form = useForm({
		defaultValues: {
			id: null,
			with_fee_exemption: "Yes",
			service_type: "File Case, Supplemental Pleadings, and Answers",
			payor_type: "Individual",
			created_at: "2025-01-10",
			court_level: "2ND_LEVEL_COURT",
			court_type: "Regional Trial Court",
			province: "Manila",
			region: "NCR",
			court_office: "MANILA CITY",
			case_category: "Criminal Cases",
			case_type: "Admiralty Cases",
			case_number: null,
			case_title: "Case Title",
			party_type: "Petitioner",
			first_name: "Juan",
			middle_initial: "D",
			last_name: "Dela Cruz",
			email: "juandc@gmail.com",
			mobile_number: "091451245123",
			condition: "Indigent Litigants",
			assessed_value: "10000",
			number_of_summons: "2",
			status: "Pending",
		},
	});

	const [feeExemption, setFeeExemption] = useState("Yes");
	const [serviceType, setServiceType] = useState(
		"File Case, Supplemental Pleadings, and Answers",
	);

	type FundSource = {
		name: string;
		filing_fee: string;
		miscellaneous_fee: string;
		total: string;
	};

	const columns: ColumnDef<FundSource>[] = [
		{
			id: "name",
			label: "Particulars",
			cell: ({ data }) => data.name,
		},
		{
			id: "filing_fee",
			label: "Filing Fee",
			cell: ({ data }) => data.filing_fee,
		},
		{
			id: "miscellaneous_fee",
			label: "Miscellaneous Fee",
			cell: ({ data }) => data.miscellaneous_fee,
		},
		{
			id: "total",
			label: "Sub Total",
			cell: ({ data }) => data.total,
		},
	];

	const fundSources: FundSource[] = [
		{
			name: "JDF",
			filing_fee: "",
			miscellaneous_fee: "PHP 3.80",
			total: "PHP 3.80",
		},
		{
			name: "SAJF",
			filing_fee: "",
			miscellaneous_fee: "PHP 6.20",
			total: "PHP 6.20",
		},
		{
			name: "Total Amount Paid",
			filing_fee: "",
			miscellaneous_fee: "",
			total: "PHP 10.0",
		},
		{
			name: "Transaction No.",
			filing_fee: "PAY1508S71XSE5124",
			miscellaneous_fee: "",
			total: "",
		},
	];

	function CaseEvidence() {
		return (
			<div className="col-span-3">
				<button
					type="button"
					className="flex flex-col w-full rounded-md border border-charcoal-100 overflow-hidden"
				>
					<div className="flex justify-center items-center w-full h-[200px] bg-charcoal-50 text-gray-800 cursor-pointer">
						<IconFileTypePdf size={24} stroke={2} />
					</div>
					<div className="flex flex-col w-full p-2 border-t border-charcoal-100">
						<span className="text-left font-semibold text-gray-800 truncate max-w-[200px] leading-tight">
							ninjago.pdf
						</span>
						<span className="text-left text-gray-400 leading-tight">
							10 Dec 2024
						</span>
					</div>
				</button>
			</div>
		);
	}

	return (
		<Content>
			<Header />
			<Tiptap />
			<Form {...form}>
				<div className="flex flex-col w-full p-4">
					<Alert
						type="info"
						allowClose={true}
						title={`Status: ${status}`}
						icon={<IconAlertCircle size={20} stroke={2} />}
					>
						{status === "deferred" && (
							<span className="flex flex-col w-full text-1xl font-bold uppercase">
								Remarks: {remarks}
							</span>
						)}
					</Alert>

					<div align="left" className="my-3">
						<span className="text-1xl font-bold">Payment Details</span>
					</div>

					<div className="col-span-12 flex flex-col w-full">
						<DataTable data={fundSources} columns={columns} />
					</div>

					<div className="col-span-12 flex flex-col w-full py-2">
						<FormField
							control={form.control}
							name="case_number"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Case Number</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input
											autoComplete="off"
											placeholder="Case Number"
											{...field}
										/>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div align="left" className="my-3">
						<span className="text-1xl font-bold">Case Details</span>
					</div>

					<div className="grid grid-cols-12 w-full gap-3">
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="service_type"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Service Type</FormLabel>
											<FormDescription>
												Some form input requires description, this is the right
												place for that.
											</FormDescription>
										</div>
										<FormGroup>
											<Select
												onValueChange={async (value) => {
													field.onChange(value);
													setServiceType(value);
												}}
												defaultValue={field.value}
												options={[
													{
														value:
															"File Case, Supplemental Pleadings, and Answers",
														label:
															"File Case, Supplemental Pleadings, and Answers",
													},
													{
														value:
															"Filing of Applications, Motions, or Requests",
														label:
															"Filing of Applications, Motions, or Requests",
													},
													{
														value: "Other Services",
														label: "Other Services",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="payor_type"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Payor Type</FormLabel>
											<FormDescription>
												Some form input requires description, this is the right
												place for that.
											</FormDescription>
										</div>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "Individual",
														label: "Individual",
													},
													{
														value: "Juridical",
														label: "Juridical",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div align="left" className="my-3">
						<span className="text-1xl font-bold">Court Details</span>
					</div>

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="court_level"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Court Level</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={async (value) => {
													field.onChange(value);
												}}
												defaultValue={field.value}
												options={[
													{
														value: "COURT_APPEALS",
														label: "Court of Appeals",
													},
													{
														value: "COURT_TAX_APPEALS",
														label: "Court of Tax Appeals",
													},
													{
														value: "SANDIGAN_BAYAN",
														label: "Sandiganbayan",
													},
													{
														value: "SUPREME_COURT",
														label: "Supreme Court",
													},
													{
														value: "2ND_LEVEL_COURT",
														label: "2nd Level Court",
													},
													{
														value: "1ST_LEVEL_COURT",
														label: "1st Level Court",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="court_type"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Court Type</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "Court of Appeals - CDO",
														label: "Court of Appeals - CDO",
													},
													{
														value: "Court of Appeals - Manila",
														label: "Court of Appeals - Manila",
													},
													{
														value: "Court of Appeals - Cebu",
														label: "Court of Appeals - Cebu",
													},
													{
														value: "Court of Tax Appeals",
														label: "Court of Tax Appeals",
													},
													{
														value: "Sandiganbayan",
														label: "Sandiganbayan",
													},
													{
														value: "Office of the Court Administrator",
														label: "Office of the Court Administrator",
													},
													{
														value:
															"Mandatory Continuing Legal Education Office",
														label:
															"Mandatory Continuing Legal Education Office",
													},
													{
														value: "Supreme Court (FMBO)",
														label: "Supreme Court (FMBO)",
													},
													{
														value: "Regional Trial Court",
														label: "Regional Trial Court",
													},
													{
														value: "Sharia District Court",
														label: "Sharia District Court",
													},
													{
														value: "Municipal Trial Court",
														label: "Municipal Trial Court",
													},
													{
														value: "Municipal Trial Court in Cities",
														label: "Municipal Trial Court in Cities",
													},
													{
														value: "Municipal Circuit Trial Court",
														label: "Municipal Circuit Trial Court",
													},
													{
														value: "Sharia Circuit Court",
														label: "Sharia Circuit Court",
													},
													{
														value: "Metropolitan Trial Court",
														label: "Metropolitan Trial Court",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="region"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Region</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "NCR",
														label: "NCR",
													},

													{
														value: "Region I",
														label: "Region I",
													},

													{
														value: "Region II",
														label: "Region II",
													},

													{
														value: "Region III",
														label: "Region III",
													},

													{
														value: "Region IV",
														label: "Region IV",
													},

													{
														value: "Region V",
														label: "Region V",
													},

													{
														value: "Region VI",
														label: "Region VI",
													},

													{
														value: "Region VII",
														label: "Region VII",
													},

													{
														value: "Region VIII",
														label: "Region VIII",
													},

													{
														value: "Region IX",
														label: "Region IX",
													},

													{
														value: "Region X",
														label: "Region X",
													},

													{
														value: "Region XI",
														label: "Region XI",
													},

													{
														value: "Region XII",
														label: "Region XII",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="province"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Province</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "Ilocos Sur",
														label: "Ilocos Sur",
													},
													{
														value: "La Union",
														label: "La Union",
													},
													{
														value: "Abra",
														label: "Abra",
													},
													{
														value: "Benguet",
														label: "Benguet",
													},
													{
														value: "Ilocos Norte",
														label: "Ilocos Norte",
													},
													{
														value: "Pangasinan",
														label: "Pangasinan",
													},
													{
														value: "Mountain Province",
														label: "Mountain Province",
													},
													{
														value: "Manila",
														label: "Manila",
													},
													{
														value: "Nueva Vizcaya",
														label: "Nueva Vizcaya",
													},
													{
														value: "Batanes",
														label: "Batanes",
													},
													{
														value: "Cagayan",
														label: "Cagayan",
													},
													{
														value: "Isabela",
														label: "Isabela",
													},
													{
														value: "Kalinga",
														label: "Kalinga",
													},
													{
														value: "Quirino",
														label: "Quirino",
													},
													{
														value: "Apayao",
														label: "Apayao",
													},
													{
														value: "Ifugao",
														label: "Ifugao",
													},
													{
														value: "Quirino",
														label: "Quirino",
													},
													{
														value: "Aurora",
														label: "Aurora",
													},
													{
														value: "Bataan",
														label: "Bataan",
													},
													{
														value: "Bulacan",
														label: "Bulacan",
													},
													{
														value: "Nueva Ecija",
														label: "Nueva Ecija",
													},
													{
														value: "Pampanga",
														label: "Pampanga",
													},
													{
														value: "Tarlac",
														label: "Tarlac",
													},
													{
														value: "Zambales",
														label: "Zambales",
													},
													{
														value: "Batangas",
														label: "Batangas",
													},
													{
														value: "Cavite",
														label: "Cavite",
													},
													{
														value: "Laguna",
														label: "Laguna",
													},
													{
														value: "Marinduque",
														label: "Marinduque",
													},
													{
														value: "Occidental Mindoro",
														label: "Occidental Mindoro",
													},
													{
														value: "Oriental Mindoro",
														label: "Oriental Mindoro",
													},
													{
														value: "Palawan",
														label: "Palawan",
													},
													{
														value: "Quezon",
														label: "Quezon",
													},
													{
														value: "Rizal",
														label: "Rizal",
													},
													{
														value: "Romblon",
														label: "Romblon",
													},
													{
														value: "Albay",
														label: "Albay",
													},
													{
														value: "Camarines Norte",
														label: "Camarines Norte",
													},
													{
														value: "Camarines Sur",
														label: "Camarines Sur",
													},
													{
														value: "Catanduanes",
														label: "Catanduanes",
													},
													{
														value: "Masbate",
														label: "Masbate",
													},
													{
														value: "Sorsogon",
														label: "Sorsogon",
													},
													{
														value: "Aklan",
														label: "Aklan",
													},
													{
														value: "Antique",
														label: "Antique",
													},
													{
														value: "Capiz",
														label: "Capiz",
													},
													{
														value: "Iloilo",
														label: "Iloilo",
													},
													{
														value: "Negros Occidental",
														label: "Negros Occidental",
													},
													{
														value: "Guimaras",
														label: "Guimaras",
													},
													{
														value: "Bohol",
														label: "Bohol",
													},
													{
														value: "Cebu",
														label: "Cebu",
													},
													{
														value: "Negros Oriental",
														label: "Negros Oriental",
													},
													{
														value: "Siquijor",
														label: "Siquijor",
													},
													{
														value: "Western Samar",
														label: "Western Samar",
													},
													{
														value: "Biliran",
														label: "Biliran",
													},
													{
														value: "Eastern Samar",
														label: "Eastern Samar",
													},
													{
														value: "Leyte",
														label: "Leyte",
													},
													{
														value: "Northern Samar",
														label: "Northern Samar",
													},
													{
														value: "Southern Leyte",
														label: "Southern Leyte",
													},
													{
														value: "Sulu",
														label: "Sulu",
													},
													{
														value: "Zamboanga del Norte",
														label: "Zamboanga del Norte",
													},
													{
														value: "Zamboanga del Sur",
														label: "Zamboanga del Sur",
													},
													{
														value: "Zamboanga Sibugay",
														label: "Zamboanga Sibugay",
													},
													{
														value: "Basilan",
														label: "Basilan",
													},
													{
														value: "Tawi-tawi",
														label: "Tawi-tawi",
													},
													{
														value: "Dinagat Islands",
														label: "Dinagat Islands",
													},
													{
														value: "Agusan del Norte",
														label: "Agusan del Norte",
													},
													{
														value: "Agusan del Sur",
														label: "Agusan del Sur",
													},
													{
														value: "Bukidnon",
														label: "Bukidnon",
													},
													{
														value: "Camiguin",
														label: "Camiguin",
													},
													{
														value: "Misamis Occidental",
														label: "Misamis Occidental",
													},
													{
														value: "Misamis Oriental",
														label: "Misamis Oriental",
													},
													{
														value: "Surigao del Norte",
														label: "Surigao del Norte",
													},
													{
														value: "Davao De Oro",
														label: "Davao De Oro",
													},
													{
														value: "Davao Occidental",
														label: "Davao Occidental",
													},
													{
														value: "Davao del Norte",
														label: "Davao del Norte",
													},
													{
														value: "Davao del Sur",
														label: "Davao del Sur",
													},
													{
														value: "Davao Oriental",
														label: "Davao Oriental",
													},
													{
														value: "Sarangani",
														label: "Sarangani",
													},
													{
														value: "South Cotabato",
														label: "South Cotabato",
													},
													{
														value: "Surigao del Sur",
														label: "Surigao del Sur",
													},
													{
														value: "North Cotabato",
														label: "North Cotabato",
													},
													{
														value: "Lanao del Norte",
														label: "Lanao del Norte",
													},
													{
														value: "Lanao del Sur",
														label: "Lanao del Sur",
													},
													{
														value: "Maguindanao",
														label: "Maguindanao",
													},
													{
														value: "Sultan Kudarat",
														label: "Sultan Kudarat",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="col-span-12 flex flex-col w-full py-2">
						<FormField
							control={form.control}
							name="court_office"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Court Station/Office</FormLabel>
										<FormDescription />
									</div>
									<FormGroup>
										<Select
											onValueChange={field.onChange}
											defaultValue={field.value}
											options={[
												{
													value: "PARAÑAQUE CITY",
													label: "PARAÑAQUE CITY",
												},
												{
													value: "MAKATI CITY",
													label: "MAKATI CITY",
												},
												{
													value: "MARIKINA CITY",
													label: "MARIKINA CITY",
												},
												{
													value: "MANDALUYONG CITY",
													label: "MANDALUYONG CITY",
												},
												{
													value: "PASIG CITY",
													label: "PASIG CITY",
												},
												{
													value: "CALOOCAN CITY",
													label: "CALOOCAN CITY",
												},
												{
													value: "LAS PIÑAS CITY",
													label: "LAS PIÑAS CITY",
												},
												{
													value: "MANILA CITY",
													label: "MANILA CITY",
												},
												{
													value: "MUNTINLUPA CITY",
													label: "MUNTINLUPA CITY",
												},
												{
													value: "NAVOTAS CITY",
													label: "NAVOTAS CITY",
												},
												{
													value: "MALABON CITY",
													label: "MALABON CITY",
												},
												{
													value: "PATEROS CITY",
													label: "PATEROS CITY",
												},
												{
													value: "SAN JUAN CITY",
													label: "SAN JUAN CITY",
												},
												{
													value: "TAGUIG CITY",
													label: "TAGUIG CITY",
												},
												{
													value: "VALENZUELA CITY",
													label: "VALENZUELA CITY",
												},
												{
													value: "PASAY CITY",
													label: "PASAY CITY",
												},
												{
													value: "QUEZON CITY",
													label: "QUEZON CITY",
												},
												{
													value: "MALABON CITY",
													label: "MALABON CITY",
												},
												{
													value: "SAN MATEO",
													label: "SAN MATEO",
												},
												{
													value: "ANGONO",
													label: "ANGONO",
												},
												{
													value: "BINANGONAN",
													label: "BINANGONAN",
												},
												{
													value: "CAINTA",
													label: "CAINTA",
												},
												{
													value: "CARDONA",
													label: "CARDONA",
												},
												{
													value: "MORONG",
													label: "MORONG",
												},
												{
													value: "RODRIGUEZ",
													label: "RODRIGUEZ",
												},
												{
													value: "TANAY",
													label: "TANAY",
												},
												{
													value: "TAYTAY",
													label: "TAYTAY",
												},
											]}
										/>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="col-span-12 flex flex-col w-full py-2">
						This Court has 4 Case Filing Request
					</div>

					{serviceType === "File Case, Supplemental Pleadings, and Answers" ? (
						<>
							<div align="left" className="my-3">
								<span className="text-1xl font-bold">Case Details</span>
							</div>

							<div className="col-span-12 flex flex-col w-full py-2">
								<FormField
									control={form.control}
									name="case_category"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Case Category</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													options={[
														{
															value: "Cadastral and Land Registration Cases",
															label: "Cadastral and Land Registration Cases",
														},
														{
															value: "Civil Cases",
															label: "Civil Cases",
														},
														{
															value: "Criminal Cases",
															label: "Criminal Cases",
														},
														{
															value: "Environmental Cases",
															label: "Environmental Cases",
														},
														{
															value: "Others",
															label: "Others",
														},
														{
															value: "Special Civil Actions",
															label: "Special Civil Actions",
														},
														{
															value: "Special Proceedings",
															label: "Special Proceedings",
														},
													]}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-12 flex flex-col w-full py-2">
								<FormField
									control={form.control}
									name="case_type"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Case Type</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													options={[
														{
															value: "Admiralty Cases",
															label: "Admiralty Cases",
														},

														{
															value:
																"Civil Actions where the Value of the Subject Matter Involved, or Amount of the Demand, inclusive of Interests, Penalties, Surcharges, Damages of Whatever Kind, Attorneys Fees, Litigation Expenses and Costs",
															label:
																"Civil Actions where the Value of the Subject Matter Involved, or Amount of the Demand, inclusive of Interests, Penalties, Surcharges, Damages of Whatever Kind, Attorneys Fees, Litigation Expenses and Costs",
														},

														{
															value:
																"Claims for Damages With or Without Provisional Remedies or Answer with Permissive Counterclaim",
															label:
																"Claims for Damages With or Without Provisional Remedies or Answer with Permissive Counterclaim",
														},
														{
															value: "Collection of Sum of Money",
															label: "Collection of Sum of Money",
														},
														{
															value:
																"Collection of Sum of Money and Replevin with Provisional Remedies (i.e. TRO, WPI or Attachment)",
															label:
																"Collection of Sum of Money and Replevin with Provisional Remedies (i.e. TRO, WPI or Attachment)",
														},
														{
															value: "Collection of Sum of Money or Replevin",
															label: "Collection of Sum of Money or Replevin",
														},
														{
															value:
																"Collection of Sum of Money or Replevin with Provisional Remedies",
															label:
																"Collection of Sum of Money or Replevin with Provisional Remedies",
														},
														{
															value: "Collection of Sum of Money with Replevin",
															label: "Collection of Sum of Money with Replevin",
														},
														{
															value: "Consignation Cases",
															label: "Consignation Cases",
														},
														{
															value:
																"Injunction with Damages and with/without Additional Provisional Remedies",
															label:
																"Injunction with Damages and with/without Additional Provisional Remedies",
														},
														{
															value: "Injunction with TRO",
															label: "Injunction with TRO",
														},
														{
															value:
																"Multiple Causes of Action - Real Actions with Damages and Other Actions, with or without Provisional Remedy",
															label:
																"Multiple Causes of Action - Real Actions with Damages and Other Actions, with or without Provisional Remedy",
														},
														{
															value:
																"Other Actions not Covered by Section 8(a) to 8(c) of Rule 141, Rules of Court (including Enforcement of Barangay Amicable Settlement) with Damages and Other Claims",
															label:
																"Other Actions not Covered by Section 8(a) to 8(c) of Rule 141, Rules of Court (including Enforcement of Barangay Amicable Settlement) with Damages and Other Claims",
														},
														{
															value:
																"Other Actions not Covered by Section 8(a) to 8(c) of Rule 141, Rules of Court (including Enforcement of Barangay Amicable Settlement) without Damages",
															label:
																"Other Actions not Covered by Section 8(a) to 8(c) of Rule 141, Rules of Court (including Enforcement of Barangay Amicable Settlement) without Damages",
														},
														{
															value:
																"Real Actions/Accion Reivindicatoria/Accion Publiciana/Recovery of Possession",
															label:
																"Real Actions/Accion Reivindicatoria/Accion Publiciana/Recovery of Possession",
														},
														{
															value: "Replevin with or without Damages",
															label: "Replevin with or without Damages",
														},
														{
															value:
																"Replevin with or without Damages, with or without Provisional Remedies (i.e. TRO, WPI or Attachment)",
															label:
																"Replevin with or without Damages, with or without Provisional Remedies (i.e. TRO, WPI or Attachment)",
														},
														{
															value:
																"Revival of Judgment - New Cause of Action is Capable of Pecuniary Estimation",
															label:
																"Revival of Judgment - New Cause of Action is Capable of Pecuniary Estimation",
														},
														{
															value:
																"Small Claims Cases Arising from Contracts and Other Credit Accommodations, Loans, etc.",
															label:
																"Small Claims Cases Arising from Contracts and Other Credit Accommodations, Loans, etc.",
														},
														{
															value:
																"Small Claims Cases for Duly Registered Engaged in the Business of Banking, Lending and Similar Activities",
															label:
																"Small Claims Cases for Duly Registered Engaged in the Business of Banking, Lending and Similar Activities",
														},
														{
															value:
																"Small Claims - Re-Filed Within 1 yr. due to Dismissal under Section 12(f), Rule IV (Rule on Small Claims), Rules on Expedited Procedures in the First Level Courts",
															label:
																"Small Claims - Re-Filed Within 1 yr. due to Dismissal under Section 12(f), Rule IV (Rule on Small Claims), Rules on Expedited Procedures in the First Level Courts",
														},
													]}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-12 flex flex-col w-full py-2">
								<FormField
									control={form.control}
									name="case_title"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Case Title/Particulars</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Textarea {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</>
					) : (
						<>
							<div align="left">
								<span className="text-1xl font-bold">Application Details</span>
							</div>
							<div className="col-span-12 flex flex-col w-full">
								<FormField
									control={form.control}
									name="collection_type"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Collection Type</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													options={[
														{
															value:
																"Appeal Fee for Accused Appellant - Sec. 8(f)",
															label:
																"Appeal Fee for Accused Appellant - Sec. 8(f)",
														},
														{
															value:
																"Appeal Fee for Barangay or Sangguniang Kabataan Election Contests - Sec. 9, AM No. 07-4-15-SC dtd. Aug. 29, 2023",
															label:
																"Appeal Fee for Barangay or Sangguniang Kabataan Election Contests - Sec. 9, AM No. 07-4-15-SC dtd. Aug. 29, 2023",
														},
														{
															value:
																"Appeal Fee for Civil Case/Complainant - Sec. 8(f)",
															label:
																"Appeal Fee for Civil Case/Complainant - Sec. 8(f)",
														},
														{
															value:
																"Application/Motion for TRO and/or Injunction - Item a(iii) of AM No. 17-12-09-SC dated 10 January 2018 (OCA Cir. No. 36-2018)",
															label:
																"Application/Motion for TRO and/or Injunction - Item a(iii) of AM No. 17-12-09-SC dated 10 January 2018 (OCA Cir. No. 36-2018)",
														},
														{
															value:
																"Certificate of Sale (Sec. 21(d)) and Commission on Highest Bid Amount (Sec. 10(L))",
															label:
																"Certificate of Sale (Sec. 21(d)) and Commission on Highest Bid Amount (Sec. 10(L))",
														},
														{
															value:
																"Certificate of Sale (Sec. 21(d)) and Commission on Highest Bid Amount (Sec. 10(L)) - NO MONEY TO BE DEPOSITED",
															label:
																"Certificate of Sale (Sec. 21(d)) and Commission on Highest Bid Amount (Sec. 10(L)) - NO MONEY TO BE DEPOSITED",
														},
														{
															value:
																"Clearance and Certification Fee (Sec. 8(o)) with/out Certified Copies (Sec. 8(k))",
															label:
																"Clearance and Certification Fee (Sec. 8(o)) with/out Certified Copies (Sec. 8(k))",
														},
														{
															value:
																"Clearance and Certification Fee (Sec. 8(o)) with Search Fee and with/out Certified Copies (Sec. 8(k))",
															label:
																"Clearance and Certification Fee (Sec. 8(o)) with Search Fee and with/out Certified Copies (Sec. 8(k))",
														},
														{
															value:
																"Filing of Indemnity or Security Cash Bond/s in Provisional Remedy - Sec. 10(f)",
															label:
																"Filing of Indemnity or Security Cash Bond/s in Provisional Remedy - Sec. 10(f)",
														},
														{
															value:
																"For entries of Final Deed of Sale - Sec. 21(d)",
															label:
																"For entries of Final Deed of Sale - Sec. 21(d)",
														},
														{
															value:
																"Motion for 1st Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 1st Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for 2nd Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 2nd Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for 3rd Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 3rd Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for 4th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 4th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for 5th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 5th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for 6th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 6th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for 7th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
															label:
																"Motion for 7th Postponement Fee for Multiple Cases - Sec. 21(b)(1)",
														},
														{
															value:
																"Motion for Postponement Fee per Case - Sec. 21(b)(1)",
															label:
																"Motion for Postponement Fee per Case - Sec. 21(b)(1)",
														},
														{
															value: "Payment of Bond Fee only - Sec. 21(c)",
															label: "Payment of Bond Fee only - Sec. 21(c)",
														},
														{
															value:
																"Reception of Evidence by the Clerk of Court - Sec. 21(e)",
															label:
																"Reception of Evidence by the Clerk of Court - Sec. 21(e)",
														},
													]}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="col-span-12 flex flex-col w-full">
								<FormField
									control={form.control}
									name="case_number"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>
													Case No./Roll/MCLE/Bar Examinee No.
												</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input
													autoComplete="off"
													placeholder="Case No./Roll/MCLE/Bar Examinee No."
													{...field}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</>
					)}

					<div align="left" className="py-3">
						<span className="text-1xl font-bold">Personal Information</span>
					</div>

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="party_type"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Party Type</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value: "Petitioner",
														label: "Petitioner",
													},
													{
														value: "Respondent",
														label: "Respondent",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-4 w-full">
							<FormField
								control={form.control}
								name="first_name"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>First Name</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input
												autoComplete="off"
												placeholder="First Name"
												{...field}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-4 w-full">
							<FormField
								control={form.control}
								name="middle_initial"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Middle Initial</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input
												autoComplete="off"
												placeholder="Middle Initial"
												{...field}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-4 w-full">
							<FormField
								control={form.control}
								name="last_name"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Last Name</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input
												autoComplete="off"
												placeholder="Last Name"
												{...field}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Email Address</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input
												autoComplete="off"
												placeholder="Email Address"
												{...field}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="mobile_number"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Mobile Number</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input
												autoComplete="off"
												placeholder="Mobile Number"
												{...field}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div align="left" className="py-3">
						<span className="text-1xl font-bold">Basis of Fees</span>
					</div>

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-4 w-full">
							<FormField
								control={form.control}
								name="with_fee_exemption"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>With Fee Exemption</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={async (value) => {
													field.onChange(value);
													setFeeExemption(value);
												}}
												defaultValue={field.value}
												options={[
													{
														value: "Yes",
														label: "Yes",
													},
													{
														value: "No",
														label: "No",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{feeExemption === "Yes" && (
						<div className="col-span-12 flex flex-col w-full py-2">
							<FormField
								control={form.control}
								name="condition"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Condition (if yes)</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
												options={[
													{
														value:
															"Indigent Clients of the Public Attorney`s Office (PAO) (OCA Circular No. 121-2007)",
														label:
															"Indigent Clients of the Public Attorney`s Office (PAO) (OCA Circular No. 121-2007)",
													},
													{
														value:
															"Petitions for Voluntary Confinement under Article VIII , Section 54 of Republic Act 9165 (A.M. No. 10-2-03-0)",
														label:
															"Petitions for Voluntary Confinement under Article VIII , Section 54 of Republic Act 9165 (A.M. No. 10-2-03-0)",
													},
													{
														value:
															"Person/s filing a Petition for Writ of Amparo (A.M. No. 07-9-12-SC)",
														label:
															"Person/s filing a Petition for Writ of Amparo (A.M. No. 07-9-12-SC)",
													},
													{
														value:
															"Indigent Litigant filing a Writ of Habeas Data (Sec. 5, The Rule on the Writ of Habeas Data, AM No. 08-1-16-SC)",
														label:
															"Indigent Litigant filing a Writ of Habeas Data (Sec. 5, The Rule on the Writ of Habeas Data, AM No. 08-1-16-SC)",
													},
													{
														value:
															"Clients of the National Committee on Legal Aid (NCLA) (OCA Circular No. 137-2009)",
														label:
															"Clients of the National Committee on Legal Aid (NCLA) (OCA Circular No. 137-2009)",
													},
													{
														value:
															"Reconstitution of Torrens Certificate of Title Lost or Destroyed and Cancellation of Encumbrance Pursuant to Sec. 23 of Republic Act No. 26",
														label:
															"Reconstitution of Torrens Certificate of Title Lost or Destroyed and Cancellation of Encumbrance Pursuant to Sec. 23 of Republic Act No. 26",
													},
													{
														value: "Indigent Litigants",
														label: "Indigent Litigants",
													},
													{
														value:
															"COMELEC filing a Petition for Exclusion of Voters (OCA Circular No. 54-2003)",
														label:
															"COMELEC filing a Petition for Exclusion of Voters (OCA Circular No. 54-2003)",
													},
													{
														value:
															"Writ of Continuing Mandamus (Sec. 3, Rule 8, Rules of Procedure for Environmental Cases, AM No. 09-6-8-SC)",
														label:
															"Writ of Continuing Mandamus (Sec. 3, Rule 8, Rules of Procedure for Environmental Cases, AM No. 09-6-8-SC)",
													},
													{
														value:
															"Republic of the Philippines, its agencies and instrumentalities (Sec. 22, A.M. No. 35-2004)",
														label:
															"Republic of the Philippines, its agencies and instrumentalities (Sec. 22, A.M. No. 35-2004)",
													},
													{
														value:
															"Writ of Kalikasan (Sec. 4, Rule 7, Rules of Procedure for Environmental Cases, AM No. 09-6-8-SC)",
														label:
															"Writ of Kalikasan (Sec. 4, Rule 7, Rules of Procedure for Environmental Cases, AM No. 09-6-8-SC)",
													},
												]}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}

					<div className="grid grid-cols-12 w-full gap-3 py-2">
						<div className="col-span-6 w-full">
							<FormField
								control={form.control}
								name="assessed_value"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>
												Assessed Value of the Property
											</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input
												autoComplete="off"
												placeholder="Assessed Value of the Property"
												{...field}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="col-span-12 flex flex-col w-full">
						<FormField
							control={form.control}
							name="number_of_summons"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>
											Number of Summons/Notices to be Issued
										</FormLabel>
										<FormDescription />
									</div>
									<FormGroup iconPosition="left">
										<Input
											autoComplete="off"
											placeholder="Number of Summons/Notices to be Issued"
											{...field}
										/>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div align="left" className="py-3">
						<span className="text-1xl font-bold">Checklists</span>
					</div>

					<div className="grid grid-cols-12 w-full gap-4">
						<CaseEvidence />
						<CaseEvidence />
						<CaseEvidence />
						<CaseEvidence />
					</div>

					<div className="flex flex-row w-full gap-2 mt-4">
						<Button
							className=""
							variant="secondary"
							onClick={() => {
								modal.openModal({
									allowClose: false,
									title: "Confirm Case Details",
									description:
										"Yes, this is for the description, Please make it short",
									size: "md",
									showHeader: true,
									showFooter: false,
									component: <CaseClassificationModal />,
								});
							}}
						>
							Confirm
						</Button>
						{status === "pending" && (
							<Button
								className=""
								variant="danger"
								onClick={() => {
									modal.openModal({
										allowClose: false,
										title: "Confirm Case Details",
										description:
											"Yes, this is for the description, Please make it short",
										size: "md",
										showHeader: true,
										showFooter: false,
										component: <CaseDeferredModal />,
									});
								}}
							>
								Deferred
							</Button>
						)}

						<Button className="" variant="ghost">
							Cancel
						</Button>
					</div>
				</div>
			</Form>
		</Content>
	);
}
